import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Category } from '../types/category';
import { getCategories } from '../api';

interface CategoryContextType {
  categories: Category[];
  loading: boolean;
  error: string | null;
  refreshCategories: () => Promise<void>;
}

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

export function CategoryProvider({ children }: { children: React.ReactNode }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false); // Start with false since we're lazy loading
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const refreshCategories = useCallback(async () => {
    console.log('[DEBUG] CategoryContext: Refresh called', {
      loading,
      isInitialized,
      hasCategories: categories.length > 0,
      stack: new Error().stack
    });

    // Skip if already loading or initialized with data
    if (loading || (isInitialized && categories.length > 0)) {
      console.log('[DEBUG] CategoryContext: Skipping fetch - already loaded');
      return;
    }
    
    try {
      console.log('[DEBUG] CategoryContext: Fetching categories');
      setLoading(true);
      setError(null);
      const fetchedCategories = await getCategories();
      console.log('[DEBUG] CategoryContext: Categories fetched', { count: fetchedCategories.length });
      setCategories(fetchedCategories);
      setIsInitialized(true);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  }, [loading, isInitialized, categories.length]);

  // Don't auto-fetch on mount, let components request data when needed

  return (
    <CategoryContext.Provider value={{ categories, loading, error, refreshCategories }}>
      {children}
    </CategoryContext.Provider>
  );
}

export function useCategories() {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
}
